/**
 * The "done" state of the sidebar checklist row (design navigation-v2 6d): a green check for 24
 * hours after the last step, then the row goes away for good.
 *
 * The server keeps no time of the last step, so this browser keeps it: the row notes that it saw
 * the checklist open, and the first time it sees the checklist complete after that, it stores the
 * time. A checklist that was complete before this browser saw it open shows no done row. The data
 * is a per-browser convenience; the storage can fail (a private window), and then the row simply
 * does not show the done state.
 */
export const CHECKLIST_DONE_ROW_MS = 24 * 60 * 60 * 1000;

const SEEN_OPEN = 'open';

function storageKey(userId: number, accountId: number): string {
  return `m24ChecklistDone:${userId}:${accountId}`;
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** False when the browser refuses the write (a full or blocked storage). */
function write(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    // No storage: the done row does not show.
    return false;
  }
}

/** Notes that this browser saw the checklist of the artist open. */
export function noteChecklistOpen(userId: number, accountId: number): void {
  const key = storageKey(userId, accountId);
  if (read(key) === null) {
    write(key, SEEN_OPEN);
  }
}

/**
 * Whether the complete checklist shows the done row now. The first call after the checklist was
 * seen open stores the time of completion.
 */
export function showsChecklistDoneRow(userId: number, accountId: number, now: number): boolean {
  const key = storageKey(userId, accountId);
  const stored = read(key);
  if (stored === null) {
    return false;
  }
  if (stored === SEEN_OPEN) {
    // With no stored time the 24 hours cannot end, so a refused write shows no done row.
    return write(key, String(now));
  }
  const doneAt = Number(stored);
  return Number.isFinite(doneAt) && now - doneAt < CHECKLIST_DONE_ROW_MS;
}
